import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { Popover } from '@blueprintjs/core';

import { Text } from 'common-components/typography';
import { ActionMenu, ActionMenuItem } from 'common-components/action-menu';
import CommonUtils from 'utilities/common-utils';
import { IRosterShift, IRosterWorker, IRosterWorkerWithShifts } from '../../types';
import { ActionIcon, Avatar, Group, Box } from '@good/ui/core';
import { MoreHoriz } from '@good/icons';
import { rosterConfig } from '../../roster-config';

interface WorkerContentRowProps {
  worker: IRosterWorker;
  assignedShifts?: IRosterWorkerWithShifts[];
}

const { TIMELINE_ROW_HEIGHT } = rosterConfig;

export function WorkersColumn({ worker, assignedShifts }: WorkerContentRowProps) {
  const history = useHistory();
  const { workerFirstName, workerLastName, supportWorkerId, workerAvatarUrl } = worker;
  const workerWithShifts = _.find(assignedShifts, (shifts) => shifts.supportWorkerId === supportWorkerId);
  const totalDuration = getTotalDurationForShifts(workerWithShifts);
  const rowHeight =
    workerWithShifts && !_.isEmpty(workerWithShifts.shifts)
      ? _.size(workerWithShifts.shifts) * TIMELINE_ROW_HEIGHT
      : TIMELINE_ROW_HEIGHT;
  const fullName = `${workerFirstName} ${workerLastName}`;

  const goToProfile = (supportWorkerId: string) => {
    history.push(`/team/details/${supportWorkerId}`);
  };

  return (
    <Box className='roster-gray bordered-right bordered-bottom anim-fade-in-fast' style={{ height: rowHeight }}>
      <Group justify='space-between' gap='xs' h='100%' wrap='nowrap' w='100%' px='lg'>
        <div className='flex-row align-center'>
          <Avatar radius='md' mr='sm' src={workerAvatarUrl} name={fullName} />
          <div className='line-height-135 flex-column'>
            <div className='whitespace-nowrap overflow-hidden text-overflow-ellipsis'>
              <Text>{fullName}</Text>
            </div>

            <div className='line-height-120 flex-row align-center'>
              <div className='whitespace-nowrap overflow-hidden text-overflow-ellipsis'>
                <Text size='regular' color='secondary' lineHeight={120}>
                  {CommonUtils.formatDurationFromSecondsString(totalDuration)}
                </Text>
              </div>
            </div>
          </div>
        </div>
        <Popover
          content={
            <ActionMenu>
              <ActionMenuItem text={'View profile'} onClick={() => goToProfile(supportWorkerId)} />
            </ActionMenu>
          }
          position='bottom-left'
        >
          <ActionIcon variant='default'>
            <MoreHoriz />
          </ActionIcon>
        </Popover>
      </Group>
    </Box>
  );
}

function getTotalDurationForShifts(workerShifts) {
  const shiftGroups = _.flatten(workerShifts.shifts);

  return _.reduce(
    shiftGroups,
    (acc, shift: IRosterShift) => {
      const { shiftStartDateTime, shiftEndDateTime } = shift;

      // difference of shiftStartDateTime & shiftEndDateTime in seconds
      const duration = moment(shiftEndDateTime).diff(moment(shiftStartDateTime), 'seconds');

      return acc + duration;
    },
    0,
  );
}
